/**
 * EssayDeletionConfirmModal - Confirmation dialog for essay slot deletions
 * Shows impact analysis and requires user confirmation before deleting
 */

import React, { useState } from 'react';
import { X, AlertTriangle, Save, Trash2, Info, CheckCircle } from 'lucide-react';

const EssayDeletionConfirmModal = ({
  isOpen,
  onClose,
  diff,
  collegeName,
  onConfirm,
  loading = false
}) => {
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen || !diff) return null;

  const { toAdd, toUpdate, toDelete, summary } = diff;

  const handleConfirm = () => {
    if (confirmed) {
      onConfirm();
    }
  };

  const totalTasksToDelete = toDelete.reduce((sum, deletion) => sum + (deletion.taskCount || 0), 0);
  const draftsWithContent = toDelete.filter(d => d.hasContent).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Confirm Essay Changes
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {collegeName}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            {summary.additions > 0 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-2xl font-bold text-green-700">{summary.additions}</div>
                <div className="text-sm text-green-600">Essay{summary.additions !== 1 ? 's' : ''} to Add</div>
              </div>
            )}
            {summary.updates > 0 && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{summary.updates}</div>
                <div className="text-sm text-blue-600">Essay{summary.updates !== 1 ? 's' : ''} to Update</div>
              </div>
            )}
            {summary.deletions > 0 && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="text-2xl font-bold text-red-700">{summary.deletions}</div>
                <div className="text-sm text-red-600">Essay{summary.deletions !== 1 ? 's' : ''} to Remove</div>
              </div>
            )}
          </div>

          {/* Additions */}
          {toAdd.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Essays to Add ({toAdd.length})
              </h4>
              <div className="space-y-2">
                {toAdd.map((addition, index) => (
                  <div key={index} className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="font-medium text-sm text-gray-900">
                      Slot #{addition.slotNumber}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {addition.prompt.substring(0, 100)}
                      {addition.prompt.length > 100 ? '...' : ''}
                    </div>
                    {addition.wordCount && (
                      <div className="text-xs text-gray-500 mt-1">
                        {addition.wordCount} words
                      </div>
                    )}
                    <div className="text-xs text-green-700 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Will create 7 timeline tasks (1 parent + 6 subtasks)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Updates */}
          {toUpdate.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Save className="w-4 h-4 text-blue-600" />
                Essays to Update ({toUpdate.length})
              </h4>
              <div className="space-y-2">
                {toUpdate.map((update, index) => (
                  <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="font-medium text-sm text-gray-900">
                      Slot #{update.slotNumber}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      <div className="line-through">
                        {update.currentPrompt.substring(0, 80)}
                      </div>
                      <div className="text-gray-700 mt-1">
                        → {update.newPrompt.substring(0, 80)}
                      </div>
                    </div>
                    {update.hasContent && (
                      <div className="text-xs text-blue-700 mt-2 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Has draft content (will be preserved)
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deletions */}
          {toDelete.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                Essays to Remove ({toDelete.length})
              </h4>
              <div className="space-y-2">
                {toDelete.map((deletion, index) => (
                  <div key={index} className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="font-medium text-sm text-gray-900">
                      Slot #{deletion.slotNumber}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {deletion.prompt.substring(0, 100)}
                      {deletion.prompt.length > 100 ? '...' : ''}
                    </div>
                    <div className="space-y-1 mt-2">
                      {deletion.hasContent && (
                        <div className="text-xs text-green-700 flex items-center gap-1">
                          <Save className="w-3 h-3" />
                          Draft content will be preserved as unslotted essay
                        </div>
                      )}
                      {deletion.taskCount > 0 && (
                        <div className="text-xs text-red-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Will delete {deletion.taskCount} timeline task{deletion.taskCount !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Important notices */}
          {toDelete.length > 0 && (
            <div className="space-y-3">
              {/* Draft preservation notice */}
              {draftsWithContent > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Save className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm text-green-900">
                        Draft Content Will Be Preserved
                      </div>
                      <div className="text-sm text-green-700 mt-1">
                        {draftsWithContent} essay{draftsWithContent !== 1 ? 's' : ''} with content will be saved as unslotted draft{draftsWithContent !== 1 ? 's' : ''} in your essays area. No student work will be lost.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Task deletion warning */}
              {totalTasksToDelete > 0 && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm text-orange-900">
                        Timeline Tasks Will Be Deleted
                      </div>
                      <div className="text-sm text-orange-700 mt-1">
                        {totalTasksToDelete} timeline task{totalTasksToDelete !== 1 ? 's' : ''} associated with removed essays will be permanently deleted. This action cannot be undone.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Confirmation checkbox */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    disabled={loading}
                    className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div className="text-sm text-gray-700">
                    I understand that {totalTasksToDelete} timeline task{totalTasksToDelete !== 1 ? 's' : ''} will be deleted{draftsWithContent > 0 ? ', but draft content will be preserved' : ''}.
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (toDelete.length > 0 && !confirmed)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              loading || (toDelete.length > 0 && !confirmed)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            {loading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {loading ? 'Applying Changes...' : 'Confirm & Apply Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EssayDeletionConfirmModal;
